import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { Accordion, Dropdown, ProgressBar } from 'react-bootstrap';
import { useAppContext } from '@/Main';
import { useTranslation } from 'react-i18next';
import { remDomain } from '@/app/common/reducers/domainSlice';
import { useDispatch } from 'react-redux';
import {
  useGetUserBudgetQuery,
  useGetUserInfoQuery,
  useSignOutMutation
} from '@/app/common/api/authApi';
import { persistor } from '@/store/store';
import { clearPersistedSelectedDomain } from '@/app/common/utils/clearPersistedSelectedDomain';
import Avatar from '@/components/common/Avatar';
import Flex from '@/components/common/Flex';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { RiDonutChartFill } from 'react-icons/ri';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';

type LimitBodyProps = {
  max?: number;
  now?: number;
  title: string;
  isDayly?: boolean;
};

const LimitBody = ({ max, now, title, isDayly }: LimitBodyProps) => {
  return (
    <div className="mb-3">
      <div className="d-flex align-items-center fs--2 justify-content-between">
        <span>
          {title}{' '}
          {isDayly && (
            <HelpTooltip
              placement="top"
              html="این محدودیت ماهانه است."
            />
          )}
        </span>
        <span style={{ direction: 'rtl' }}>
          {now} / {max}
        </span>
      </div>
      <ProgressBar
        variant="primary"
        now={now}
        max={max}
        style={{ height: '5px' }}
        className="my-1"
      />
    </div>
  );
};

const ProfileDropdown = () => {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const [isLimitMenuOpen, setIsLimitMenuOpen] = useState(false);
  const dispatch = useDispatch();
  const [signOut] = useSignOutMutation();
  const { i18n } = useTranslation();
  const { setConfig } = useAppContext();
  const { data: userInfo, isLoading } = useGetUserInfoQuery();

  const { data: userBudget, isFetching: isFetchingUserBudget } =
    useGetUserBudgetQuery(null, {
      skip: !isLimitMenuOpen
    });

  return (
    <Dropdown id="profileDropdown" navbar as="li">
      {!isLoading && userInfo ? (
        <Dropdown.Toggle
          bsPrefix="toggle"
          as="div"
          className="pe-0 ps-2 nav-link cursor-pointer"
        >
          <Flex alignItems="center" tag="h5" className="fs--1 gap-1">
            <div className="d-md-block p-0 m-0 d-none">
              {userInfo?.data?.first_name} {userInfo?.data?.last_name}&nbsp;
              <br />
              <p className="text-secondary my-1 fs--2">
                ({userInfo?.data?.plan ? userInfo?.data?.plan?.name : 'بدون طرح'}
                )
              </p>
            </div>
            <Avatar src={userInfo?.data?.image} />
          </Flex>
        </Dropdown.Toggle>
      ) : isMdUp ? (
        <div style={{ padding: '6px 6px 6px 0px' }}>
          <TextLoading height={38.5} width={116} isWidthPx />
        </div>
      ) : (
        <div style={{ padding: '6px' }}>
          <TextLoading height={32} width={32} isWidthPx />
        </div>
      )}

      <Dropdown.Menu
        style={{ width: 250 }}
        className="dropdown-caret dropdown-menu-card dropdown-menu-end"
      >
        <div className="bg-white rounded-2 py-2 dark__bg-1000">
          <Dropdown.Item
            className="link-600 fs--1 d-flex align-items-center gap-2"
            onClick={() => {
              setConfig('isRTL', true);
              i18n.changeLanguage('fa');
            }}
          >
            <span>فارسی</span>
          </Dropdown.Item>
          <Dropdown.Item
            disabled
            className="link-600 fs--1 d-flex align-items-center gap-2"
            onClick={() => {
              setConfig('isRTL', false);
              i18n.changeLanguage('en');
            }}
          >
            <span>English (بزودی)</span>
          </Dropdown.Item>
          <Dropdown.Divider />
          <Accordion
            defaultActiveKey={[]}
            onSelect={eventKey =>
              setIsLimitMenuOpen(
                eventKey === '0' || (Array.isArray(eventKey) && eventKey.includes('0'))
              )
            }
            className=""
          >
            <Accordion.Item eventKey="0">
              <Accordion.Header
                onClick={e => e.stopPropagation()}
                as={Dropdown.Item}
                className="dropdown-item px-2 link-600 fs--1 d-flex align-items-center gap-2"
              >
                <RiDonutChartFill className="fs-0 mx-1" />
                میزان استفاده
              </Accordion.Header>
              <Accordion.Body className="px-3">
                <LimitBody
                  title="اشتراک"
                  now={
                    isFetchingUserBudget ||
                    isNaN(
                      (userBudget?.data?.panel_duration ?? 0) -
                        (userBudget?.data?.remaining_days_from_plan ?? 0)
                    )
                      ? 0
                      : (userBudget?.data?.panel_duration ?? 0) -
                        (userBudget?.data?.remaining_days_from_plan ?? 0)
                  }
                  max={userBudget?.data?.panel_duration}
                />
                <LimitBody
                  title="دامنه"
                  now={userBudget?.data?.domains?.num_of_used}
                  max={userBudget?.data?.domains?.total_budget}
                />
                <LimitBody
                  title="ردیاب کلمات"
                  now={userBudget?.data?.keywords?.num_of_used}
                  max={userBudget?.data?.keywords?.total_budget}
                />
                <LimitBody
                  isDayly
                  title="تحقیق کلمات (کیورد پلنر)"
                  now={userBudget?.data?.keyword_planner?.num_of_used}
                  max={userBudget?.data?.keyword_planner?.total_budget}
                />
                <LimitBody
                  isDayly
                  title="تحقیق کلمات (میزان جستجو)"
                  now={userBudget?.data?.search_volume?.num_of_used}
                  max={userBudget?.data?.search_volume?.total_budget}
                />
                <LimitBody
                  isDayly
                  title="ابزار SERP"
                  now={userBudget?.data?.serp?.num_of_used}
                  max={userBudget?.data?.serp?.total_budget}
                />
                <LimitBody
                  isDayly
                  title="بک لینک"
                  now={userBudget?.data?.backlink?.num_of_used}
                  max={userBudget?.data?.backlink?.total_budget}
                />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          <Dropdown.Divider />
          <Accordion defaultActiveKey={[]} className="">
            <Accordion.Item eventKey="0">
              <Accordion.Header
                onClick={e => e.stopPropagation()}
                as={Dropdown.Item}
                className="dropdown-item px-2 link-600 fs--1 d-flex align-items-center gap-2"
              >
                <FontAwesomeIcon
                  style={{
                    paddingRight: 6,
                    paddingLeft: 6
                  }}
                  icon="user"
                />
                حساب کاربری
              </Accordion.Header>
              <Accordion.Body className="px-0 py-2 pb-0">
                <Dropdown.Item
                  className="link-600 fs--1 d-flex align-items-center gap-2"
                  as={Link}
                  to={`${import.meta.env.VITE_ACCOUNT_SERVER_URL}/user`}
                >
                  <span className="ms-4">اطلاعات کاربری</span>
                </Dropdown.Item>
                <Dropdown.Item
                  className="link-600 fs--1 d-flex align-items-center gap-2"
                  as={Link}
                  to={`${import.meta.env.VITE_ACCOUNT_SERVER_URL}/phone`}
                >
                  <span className="ms-4">شماره موبایل</span>
                </Dropdown.Item>
                <Dropdown.Item
                  className="link-600 fs--1 d-flex align-items-center gap-2"
                  as={Link}
                  to={`${import.meta.env.VITE_ACCOUNT_SERVER_URL}/email`}
                >
                  <span className="ms-4">آدرس ایمیل</span>
                </Dropdown.Item>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          <Dropdown.Divider />
          <Dropdown.Item
            style={{
              paddingRight: 12,
              paddingLeft: 8
            }}
            className="link-600 fs--1 d-flex align-items-center"
            onClick={async () => {
              // پاک کردن دامنه انتخاب‌شده از Redux
              dispatch(remDomain());
              // اطمینان از نوشته‌شدن مقدار خالی در localStorage قبل از ریدایرکت logout
              await persistor.flush();
              await clearPersistedSelectedDomain();
              signOut();
            }}
          >
            <FontAwesomeIcon
              style={{
                paddingLeft: 6
              }}
              icon="sign-out-alt"
            />
            خروج از حساب
          </Dropdown.Item>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ProfileDropdown;
